// ©2024 Austin App House. All rights reserved.
//
// Super-admin companies API (frontend contract).
//
// PATCH /api/admin/companies/:companyId/suspension
//   body:  { suspended: boolean }
//     - When suspended=true the backend must block ALL users on that company
//       from logging in (login should be rejected for suspended companies).
//       When suspended=false, normal login resumes.
//       (Email to the company owner on suspend/unsuspend is TBD.)
//   res:   { success, message, suspended }
//
// GET /api/admin/companies/:companyId/users
//   res:   { payload: CompanyUser[] }
//     CompanyUser: { _id, firstname, lastname, email, phone, accessctrl, status,
//                    isAccountOwner?: boolean }
//
// PATCH /api/admin/companies/:companyId/account-owner
//   body:  { ownerUserId: string }
//     - Reassigns the company account owner to another company user.
//     - The backend should also AUTO-REASSIGN the owner to another Admin (or
//       Manager) user if the current owner/creator is deleted.
//   res:   { success, message }

import axios from 'axios';

export const ADMIN_COMPANIES = '/api/admin/companies';

export function getCompanyId(company) {
  return company?._id || company?.id || company?.companyId;
}

export function isCompanySuspended(company) {
  return Boolean(company?.suspended);
}

export function normalizeCompanyUser(user) {
  return {
    _id: user._id || user.id,
    id: user._id || user.id,
    companyId: user.companyId,
    email: user.email,
    phone: user.phone,
    firstName: user.firstname ?? user.firstName ?? '',
    lastName: user.lastname ?? user.lastName ?? '',
    role: user.accessctrl || user.role || 'Manager',
    status: user.status,
    isAccountOwner: Boolean(user.isAccountOwner),
  };
}

export async function updateCompanySuspension(companyId, suspended) {
  const { data } = await axios.patch(
    `${ADMIN_COMPANIES}/${companyId}/suspension`,
    { suspended: Boolean(suspended) },
  );
  return data;
}

export async function fetchCompanyUsers(companyId) {
  const { data } = await axios.get(`${ADMIN_COMPANIES}/${companyId}/users`);
  const list = data?.payload ?? data?.users ?? [];
  return list.map(normalizeCompanyUser);
}

export async function changeCompanyAccountOwner(companyId, ownerUserId) {
  const { data } = await axios.patch(
    `${ADMIN_COMPANIES}/${companyId}/account-owner`,
    { ownerUserId },
  );
  return data;
}
